import React, { useState } from 'react';
import { Box, Container, Typography, Button, Tabs, Tab, Alert, Card, CardContent, Grid } from '@mui/material';
import { Description as DescriptionIcon, Email as EmailIcon, Warning as WarningIcon } from '@mui/icons-material';
import CertificateRequestList from './CertificateRequestList';
import CertificateRequestForm from './CertificateRequestForm';

const CertificateRequestsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCreateNew = () => {
    setSelectedRequest(null);
    setFormOpen(true);
  };

  const handleEditRequest = (request) => {
    setSelectedRequest(request);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    setSelectedRequest(null);
    // Refresh the list without page reload
    // The parent component should handle the refetch
  };

  const tabLabels = [
    { label: 'All Requests', value: 0 },
    { label: 'Pending', value: 1 },
    { label: 'Completed', value: 2 },
    { label: 'Cancelled', value: 3 }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h3" component="h1">
            Certificate Request Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            onClick={handleCreateNew}
          >
            New Certificate Request
          </Button>
        </Box>

        {/* Workflow Information Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Certificate Request Workflow
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="body2">
                    <strong>1. Select Client & Policy</strong><br />
                    Choose client and appropriate insurance policy
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="primary" />
                  <Typography variant="body2">
                    <strong>2. Send Email Request</strong><br />
                    Email sent to agent email from policy settings
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="body2">
                    <strong>3. Monitor Response</strong><br />
                    Check Gmail for agent response and certificate
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="success" />
                  <Typography variant="body2">
                    <strong>4. Upload Certificate</strong><br />
                    Upload received certificate to link with client & policy
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Important Notes:
          </Typography>
          <Typography variant="body2" component="div">
            • Certificate requests are for "Additional Insured" status for clients<br />
            • Emails are sent to the agent email address configured in each insurance policy<br />
            • You must manually monitor Gmail responses and update request statuses<br />
            • Upload received certificates to link them to the specific client and policy<br />
            • The system tracks the complete workflow from request to certificate receipt
          </Typography>
        </Alert>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {tabLabels.map((tab) => (
              <Tab key={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && (
            <CertificateRequestList 
              onEdit={handleEditRequest}
            />
          )}
          {activeTab === 1 && (
            <CertificateRequestList 
              statusFilter="pending"
              onEdit={handleEditRequest}
            />
          )}
          {activeTab === 2 && (
            <CertificateRequestList 
              statusFilter="completed"
              onEdit={handleEditRequest}
            />
          )}
          {activeTab === 3 && (
            <CertificateRequestList 
              statusFilter="cancelled"
              onEdit={handleEditRequest}
            />
          )}
        </Box>

        <CertificateRequestForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          request={selectedRequest}
          onSuccess={handleFormSuccess}
        />
      </Box>
    </Container>
  );
};

export default CertificateRequestsPage;
